import { formatOrderRef } from '@misumi/shared'
import { Card } from '../../../components/ui/Card'
import { Chip } from '../../../components/ui/Chip'
import { ORDER_STATUS_CHIP_META } from '../../orders/domain'
import type { Shipment } from '../../orders/domain'
import { StatusIcon } from '../../orders/statusIcon'
import { formatDateIso } from '../format'
import { formatPrice } from '../format'
import type { BomOrderCardLine, BomOrderCard as BomOrderCardModel } from '../types'

export interface BomOrderHistoryCardProps {
  readonly order: BomOrderCardModel
}

/** UID-381 — no `supplier` field exists on the contract (this BOM feature
 * only ever sources from MISUMI today), so the header's "Supplier:" segment
 * is a hardcoded literal rather than a wire value. Revisit if/when a
 * multi-supplier BOM order ever ships. */
const SUPPLIER_NAME = 'MISUMI'

/**
 * UID-326 (ADR-0108) — one card per placed order in the BOM detail's Order
 * History tab, replacing the earlier flat `<table className="parts-table">`.
 * Reuses the orders vertical's status-pill taxonomy (`ORDER_STATUS_CHIP_META`
 * + `StatusIcon`, `verticals/orders/domain.ts`/`statusIcon.tsx`) so an order's
 * chip never drifts from how the standalone Orders list/detail render the
 * SAME `OrderStatus` value — and derives "Delivered {date}" / tracking from
 * `shipments[]` rather than the contract carrying separate fields (the
 * contract deliberately omits `deliveredOn`/`tracking`, see `BomOrderCard`'s
 * doc comment in `../types.ts`).
 *
 * `total` renders unlabeled (UID-381 — design drops the "Subtotal" caption,
 * showing only the amount) but IS still a server-derived subset merchandise
 * subtotal of only this BOM's linked lines, never the whole order's charged
 * total.
 */
export function BomOrderHistoryCard({ order }: BomOrderHistoryCardProps) {
  const statusMeta = ORDER_STATUS_CHIP_META[order.status]
  const deliveredShipment = resolveDeliveredShipment(order.shipments)
  const trackingShipment = resolveTrackingShipment(order.shipments)
  const heading = order.poReference ?? formatOrderRef(order.orderId)

  return (
    <Card className="bom-order-history-card">
      {/* Design ref (`Fictiv Platform.dc.html` ~L2009-2012): the header is a
          distinct tinted region (background + bottom divider) that owns the
          PO/status heading, the "Ordered … By …" subline, and the supplier/
          tracking line — all as children of ONE header element, with the total
          floated to the top-right. The card itself is `padding:0;overflow:hidden`
          (see `.bom-order-history-card` in parts.css) so this tint spans
          edge-to-edge. */}
      <div className="bom-order-history-card-header">
        <div className="bom-order-history-card-header-main">
          <div className="bom-order-history-card-heading-group">
            <h3 className="bom-order-history-card-heading">{heading}</h3>
            <Chip tone={statusMeta.tone} icon={<StatusIcon iconKey={statusMeta.iconKey} />}>
              {statusMeta.label}
            </Chip>
          </div>

          <p className="bom-order-history-card-subline caption-text">
            Ordered {formatDateIso(order.orderedOn)} · By {order.orderedBy}
            {deliveredShipment?.deliveredAt && (
              <> · Delivered {formatDateIso(deliveredShipment.deliveredAt)}</>
            )}
          </p>

          {/* UID-381 — the "Supplier: MISUMI · Tracking: …" line folding the
              old standalone "Tracking: ..." caption into a single low-emphasis
              line alongside a hardcoded "Supplier: MISUMI" segment (no supplier
              field on the contract). The tracking number renders as plain text,
              NOT a link: the contract carries no `trackingUrl`, and per product
              we do not fabricate an external destination (e.g. a carrier or
              third-party tracker). Re-render as a real `<a>` only once a
              first-party `trackingUrl` exists on the contract. */}
          <p className="bom-order-history-card-supplier-tracking caption-text">
            Supplier: {SUPPLIER_NAME}
            {trackingShipment?.trackingNumber && (
              <>
                {' · '}
                Tracking:{' '}
                <span className="bom-order-history-card-tracking-value">
                  {trackingShipment.trackingNumber}
                </span>
              </>
            )}
          </p>
        </div>
        <div className="bom-order-history-card-subtotal">
          <span className="bom-order-history-card-subtotal-value">{formatPrice(order.total)}</span>
        </div>
      </div>

      <div className="bom-order-history-card-body">
        <h4 className="bom-order-history-card-section-heading">Parts ordered</h4>
        <ul className="bom-order-history-card-lines">
          {order.lines.map((line, index) => (
            <BomOrderHistoryCardLineRow
              // No stable id on a `BomOrderCardLine` — a part number + index
              // composite is stable across re-renders of the SAME order (the
              // list never reorders itself), and index alone would collide
              // across different orders' identically-indexed lines if this
              // key were ever hoisted, so partNumber/name is included too.
              key={`${line.partNumber ?? line.name}-${index}`}
              line={line}
            />
          ))}
        </ul>
      </div>
    </Card>
  )
}

function BomOrderHistoryCardLineRow({ line }: { readonly line: BomOrderCardLine }) {
  return (
    <li className="bom-order-history-card-line">
      {line.thumbnailUrl ? (
        <img src={line.thumbnailUrl} alt="" className="bom-order-history-card-line-thumb" />
      ) : (
        <span className="bom-order-history-card-line-thumb-placeholder" aria-hidden="true" />
      )}
      <div className="bom-order-history-card-line-info">
        <span className="bom-order-history-card-line-name">{line.name}</span>
        {line.partNumber && (
          <span className="caption-text bom-order-history-card-line-part-number">
            {line.partNumber}
          </span>
        )}
      </div>
      <span className="bom-order-history-card-line-qty">{formatLineQuantity(line.quantity)}</span>
      <span className="bom-order-history-card-line-subtotal">{formatPrice(line.lineSubtotal)}</span>
    </li>
  )
}

/** "{n}×" — trims a whole-number quantity to an integer (a fractional BOM
 * quantity, e.g. 2.5m of stock, still renders its decimal rather than being
 * truncated). */
function formatLineQuantity(quantity: number): string {
  return `${Number.isInteger(quantity) ? quantity : quantity.toFixed(2)}×`
}

/** The shipment whose delivery date drives the card's "· Delivered {date}"
 * sub-line — the first (there should only ever be one for a fully-delivered
 * order) shipment that has actually reached `delivered` AND carries a
 * `deliveredAt` stamp. A `delivered` shipment with no recorded date (a data
 * gap) is treated the same as "not delivered yet" here, matching
 * `resolveShipmentHeadlineDate`'s own delivered-with-no-date posture in
 * `verticals/orders/domain.ts`. */
function resolveDeliveredShipment(shipments: readonly Shipment[]): Shipment | null {
  return (
    shipments.find((shipment) => shipment.status === 'delivered' && shipment.deliveredAt) ?? null
  )
}

/** The shipment the card's tracking line reads from — the first shipment
 * that has actually shipped (or later) AND carries a tracking number,
 * mirroring `ShipmentsPanel`'s own `showTracking` gate (tracking is only
 * meaningful once a shipment has left the building). */
function resolveTrackingShipment(shipments: readonly Shipment[]): Shipment | null {
  return (
    shipments.find(
      (shipment) =>
        !!shipment.trackingNumber &&
        (shipment.status === 'shipped' ||
          shipment.status === 'in_transit' ||
          shipment.status === 'delivered'),
    ) ?? null
  )
}
